import { useGetMeApi } from 'eds-common/api/v2';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { HeaderCard } from '@data/components/composite/HeaderCard/HeaderCard';
import { ProductCard } from '@data/components/composite/ProductCard/ProductCard';
import { LoadingBar } from '@data/components/basic/LoadingBar/LoadingBar';
import type { Application } from '@data/hooks/api/use-applications-api/use-applications-api';
import { useApplicationsHierarchy } from '@data/hooks/api/use-applications-hierarchy-api/use-applications-hierarchy-api';
import {
  getAdminReportsForProduct,
  isAdminSupportedProduct,
} from '@data/config/admin-reports';
import { getGradeDisplay } from '@data/utils/get-grade-range';
import { getAccessTags } from '@data/utils/get-access-tags';
import { capitalizeWords } from '@data/utils/capitalize-words';
import { ComingSoonPage } from '@data/pages/ComingSoonPage/ComingSoonPage';

/**
 * The application's icon, falling back to its first product thumbnail.
 *
 * The hierarchy endpoint sends `applicationIconSrc: null` for some apps (R4R,
 * KL, Scholastic Literacy) even though the shared type says string, so the
 * check is for any falsy value.
 */
function getApplicationIconSrc(
  application: Pick<Application, 'applicationIconSrc' | 'products'>
): string | undefined {
  return (
    application.applicationIconSrc ||
    (application.products ?? []).find((product) => product.thumbnailUrl)
      ?.thumbnailUrl
  );
}

function ChartIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-6 w-6 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <rect x="3" y="15" width="4" height="6" rx="1" />
      <rect x="10" y="10" width="4" height="11" rx="1" />
      <rect x="17" y="4" width="4" height="17" rx="1" />
    </svg>
  );
}

function ProductCardSkeleton() {
  return (
    <div className="rounded-lg bg-white px-4 py-6 shadow-md tablet:p-5">
      <LoadingBar className="mb-4 h-10 w-28" />
      <LoadingBar className="mb-3 h-5 w-40" />
      <LoadingBar className="mb-2 h-4 w-64" />
      <LoadingBar className="h-4" />
    </div>
  );
}

/**
 * The admin Overview report for an application, when the admin config offers
 * Overview for the product codes held. Undefined otherwise, so the card renders
 * without a link. (The admin view only renders with `dndShowAdminReport` on, so
 * the reports are available whenever this is called.)
 */
function getOverviewPath(
  app: Pick<Application, 'applicationCode' | 'products'>
): string | undefined {
  const productCodes = (app.products ?? []).map((p) => p.productCode);
  const hasOverview = getAdminReportsForProduct(
    app.applicationCode,
    productCodes
  ).some((report) => report.path === 'overview');
  return hasOverview ? `/data/${app.applicationCode}/overview` : undefined;
}

/** The admin product list, shown once the admin reports are switched on. */
function AdminProductLanding() {
  const { data: user } = useGetMeApi();
  const { data: applications, isLoading } = useApplicationsHierarchy();

  const visibleApps = applications?.filter((app) =>
    isAdminSupportedProduct(app.applicationCode)
  );

  const orgName = user?.currentOrg?.orgName;

  return (
    <div className="flex-1 bg-brand-grey_7 antialiased">
      <div className="mx-auto flex w-full max-w-[1440px] flex-col py-6 mobile:px-4 tablet:px-10">
        <HeaderCard accentClassName="h-2 from-[#A81A8E] via-[#E0204F] to-[#F25112]">
          <div className="flex flex-col gap-1">
            <h1 className="flex items-center gap-2 text-xl font-semibold text-neutral-1300 tablet:text-2xl">
              <ChartIcon />
              Data and Insights
            </h1>
            {orgName && (
              <p className="text-sm font-semibold text-neutral-1300">
                {capitalizeWords(orgName)}
              </p>
            )}
          </div>
        </HeaderCard>

        {isLoading && (
          <div
            role="status"
            aria-label="Loading products"
            className="grid grid-cols-1 gap-4 tablet:grid-cols-2 tablet:gap-5"
          >
            <ProductCardSkeleton />
            <ProductCardSkeleton />
          </div>
        )}

        {visibleApps?.length === 0 && (
          <div className="rounded-lg border-[1px] border-dashed border-neutral-400 px-6 py-8 tablet:p-10">
            <h2 className="text-xl font-bold tablet:text-2xl">
              No products yet
            </h2>
            <p className="mt-2 text-sm text-neutral-1300 tablet:text-lg">
              Products your district subscribes to will appear here.
            </p>
          </div>
        )}

        {visibleApps && visibleApps.length > 0 && (
          <section aria-label="Products">
            <ul className="grid grid-cols-1 gap-4 tablet:grid-cols-2 tablet:gap-5">
              {visibleApps.map((app) => (
                <li key={app.applicationCode}>
                  <ProductCard
                    applicationCode={app.applicationCode}
                    applicationLabel={app.applicationLabel}
                    applicationIconSrc={getApplicationIconSrc(app)}
                    gradeDisplay={getGradeDisplay(app)}
                    tags={getAccessTags(app)}
                    description={app.applicationDescription}
                    to={getOverviewPath(app)}
                  />
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}

/**
 * The Data and Insights landing page for a user on the admin experience, at
 * /data/product-landing (the route's `ByRole` picks it over
 * `ProductLandingPage`).
 *
 * Lists the products held across the admin's org hierarchy (e.g. every school
 * in the district) that the admin dashboard supports, per
 * `config/admin-reports`. A card links to the product's admin Overview report
 * when that report is available.
 *
 * Gated by `dndShowAdminReport`, like `AdminDataSection`: flag off shows the
 * Coming Soon page. (Admins-by-permission only reach this page with the flag
 * on; administrators by role reach it either way.)
 */
export function AdminProductLandingPage() {
  const { dndShowAdminReport } = useFlags();

  return dndShowAdminReport ? <AdminProductLanding /> : <ComingSoonPage />;
}
